import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Baby, Save, Skull } from 'lucide-react';

export const BirthEditDialog = ({ open, onOpenChange, selectedBirth, editFormData, setEditFormData, onSubmit, sheep, breeds, editCreerAgneaux, setEditCreerAgneaux, editAgneauxData, setEditAgneauxData, initializeEditAgneaux, updateEditAgneau }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Modifier la naissance</DialogTitle>
        <DialogDescription>
          {selectedBirth?.statut === 'ne' 
            ? 'Cette naissance est confirmée. Seules les notes peuvent être modifiées.'
            : 'Modifiez les informations de cette naissance'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        
        {/* Info lecture seule si deja ne */}
        {selectedBirth?.statut === 'ne' && (
          <div className="bg-slate-50 dark:bg-slate-900/30 border border-gray-200 dark:border-gray-800 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200 font-medium">
              <span>Naissance confirmée</span>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 grid grid-cols-2 gap-2">
              <div><strong>Date:</strong> {selectedBirth.date_reelle || selectedBirth.date_prevue}</div>
              <div><strong>Agneaux:</strong> {(selectedBirth.agneaux_ids?.length || 0) + (selectedBirth.agneaux_morts || 0)}</div>
              {selectedBirth.agneaux_ids?.length > 0 && (
                <div className="col-span-2"><strong>Agneaux créés:</strong> {selectedBirth.agneaux_ids.length}</div>
              )}
            </div>
          </div>
        )}

        {/* Agneaux morts - toujours modifiable pour naissances confirmees */}
        {selectedBirth?.statut === 'ne' && (
          <div className="space-y-2">
            <Label htmlFor="agneaux_morts_confirmed" className="flex items-center gap-2">
              <Skull className="w-4 h-4 text-red-500" />
              Agneaux morts (mort-nés ou décédés peu après)
            </Label>
            <Input
              id="agneaux_morts_confirmed"
              type="number"
              min="0"
              max={selectedBirth?.nombre_agneaux || 10}
              value={editFormData.agneaux_morts || 0}
              onChange={(e) => setEditFormData({ ...editFormData, agneaux_morts: parseInt(e.target.value) || 0 })}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Combien d'agneaux sont morts (mort-nés ou décédés peu après) ?
            </p>
          </div>
        )}

        {/* Lier moutons existants */}
        {selectedBirth?.statut === 'ne' && (
          <LinkedSheepSection selectedBirth={selectedBirth} editFormData={editFormData} setEditFormData={setEditFormData} sheep={sheep} />
        )}

        {/* Champs modifiables si pas encore ne */}
        {selectedBirth?.statut !== 'ne' && (
          <EditableFields
            selectedBirth={selectedBirth}
            editFormData={editFormData}
            setEditFormData={setEditFormData}
            editCreerAgneaux={editCreerAgneaux}
            setEditCreerAgneaux={setEditCreerAgneaux}
            editAgneauxData={editAgneauxData}
            initializeEditAgneaux={initializeEditAgneaux}
            updateEditAgneau={updateEditAgneau}
            breeds={breeds}
          />
        )}

        {/* Notes */}
        <div className="space-y-2">
          <Label htmlFor="edit-notes">Notes</Label>
          <Textarea
            id="edit-notes"
            value={editFormData.notes}
            onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
            rows={3}
            placeholder="Notes sur cette naissance..."
            data-testid="birth-edit-notes-input"
          />
        </div>
        
        <div className="flex gap-3 pt-4">
          <Button type="submit" className="flex-1 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600" data-testid="birth-update-button">
            <Save className="w-4 h-4 mr-2" />
            {selectedBirth?.statut === 'ne' 
              ? 'Enregistrer les notes'
              : editCreerAgneaux 
                ? `Confirmer et créer ${editAgneauxData.length} agneau(x)` 
                : 'Modifier la naissance'
            }
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

const LinkedSheepSection = ({ selectedBirth, editFormData, setEditFormData, sheep }) => (
  <div className="space-y-2">
    <Label className="flex items-center gap-2">
      <Baby className="w-4 h-4 text-blue-500" />
      Moutons liés à cette naissance
    </Label>
    {selectedBirth?.agneaux_ids?.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedBirth.agneaux_ids.map(aid => {
          const linkedSheep = sheep.find(s => s.id === aid);
          return linkedSheep ? (
            <span key={aid} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              linkedSheep.statut === 'mort' ? 'bg-red-100 text-red-700' : 
              linkedSheep.statut === 'vendu' ? 'bg-amber-100 text-amber-700' : 
              'bg-green-100 text-green-700'
            }`}>
              {linkedSheep.numero} - {linkedSheep.nom || 'Sans nom'}
              {linkedSheep.statut === 'mort' && ' \u2020'}
              {linkedSheep.statut === 'vendu' && ' $'}
            </span>
          ) : null;
        })}
      </div>
    )}
    <Select 
      value="" 
      onValueChange={(value) => {
        if (value && !editFormData.agneaux_ids?.includes(value)) {
          const currentIds = editFormData.agneaux_ids || selectedBirth?.agneaux_ids || [];
          setEditFormData({ ...editFormData, agneaux_ids: [...currentIds, value] });
        }
      }}
    >
      <SelectTrigger><SelectValue placeholder="Lier un mouton existant..." /></SelectTrigger>
      <SelectContent>
        {sheep
          .filter(s => !selectedBirth?.agneaux_ids?.includes(s.id) && !editFormData.agneaux_ids?.includes(s.id))
          .map(s => (
            <SelectItem key={s.id} value={s.id}>
              {s.numero} - {s.nom || 'Sans nom'} ({s.sexe === 'male' ? 'M' : 'F'} {s.statut})
            </SelectItem>
          ))
        }
      </SelectContent>
    </Select>
    <p className="text-xs text-gray-500 dark:text-gray-400">
      Liez les moutons créés à partir de cette naissance pour suivre automatiquement leur statut
    </p>
    {editFormData.agneaux_ids && editFormData.agneaux_ids.length > (selectedBirth?.agneaux_ids?.length || 0) && (
      <div className="flex flex-wrap gap-2 mt-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">À ajouter:</span>
        {editFormData.agneaux_ids
          .filter(id => !selectedBirth?.agneaux_ids?.includes(id))
          .map(aid => {
            const linkedSheep = sheep.find(s => s.id === aid);
            return linkedSheep ? (
              <span key={aid} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                {linkedSheep.numero}
                <button 
                  type="button"
                  onClick={() => setEditFormData({ 
                    ...editFormData, 
                    agneaux_ids: editFormData.agneaux_ids.filter(id => id !== aid) 
                  })}
                  className="ml-1 text-blue-500 hover:text-blue-700"
                >
                  \u2715
                </button>
              </span>
            ) : null;
          })}
      </div>
    )}
  </div>
);

const EditableFields = ({ selectedBirth, editFormData, setEditFormData, editCreerAgneaux, setEditCreerAgneaux, editAgneauxData, initializeEditAgneaux, updateEditAgneau, breeds }) => (
  <>
    <div className="space-y-2">
      <Label htmlFor="date_reelle">Date réelle de naissance</Label>
      <Input
        id="date_reelle"
        type="date"
        value={editFormData.date_reelle}
        onChange={(e) => setEditFormData({ ...editFormData, date_reelle: e.target.value })}
        data-testid="birth-date-reelle-input"
      />
      <p className="text-xs text-gray-500 dark:text-gray-400">Sera automatiquement remplie avec la date du jour si vous passez le statut à "Née"</p>
    </div>
    <div className="space-y-2">
      <Label htmlFor="statut">Statut *</Label>
      <Select
        value={editFormData.statut}
        onValueChange={(value) => {
          const today = new Date().toISOString().split('T')[0];
          const newFormData = { 
            ...editFormData, 
            statut: value,
            date_reelle: value === 'ne' ? (editFormData.date_reelle || today) : editFormData.date_reelle
          };
          setEditFormData(newFormData);
          if (value === 'ne' && (!selectedBirth?.agneaux_ids || selectedBirth.agneaux_ids.length === 0)) {
            setEditCreerAgneaux(false);
            initializeEditAgneaux(selectedBirth?.nombre_agneaux || 1, selectedBirth);
          } else {
            setEditCreerAgneaux(false);
          }
        }}
      >
        <SelectTrigger data-testid="birth-statut-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="prevue">Prévue</SelectItem>
          <SelectItem value="ne">Née</SelectItem>
          <SelectItem value="complication">Complication</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {editFormData.statut === 'ne' && (
      <div className="space-y-2">
        <Label htmlFor="agneaux_morts" className="flex items-center gap-2">
          <Skull className="w-4 h-4 text-red-500" />
          Agneaux morts (mort-nés ou décédés)
        </Label>
        <Input
          id="agneaux_morts"
          type="number"
          min="0"
          max={selectedBirth?.nombre_agneaux || 10}
          value={editFormData.agneaux_morts || 0}
          onChange={(e) => setEditFormData({ ...editFormData, agneaux_morts: parseInt(e.target.value) || 0 })}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Combien d'agneaux sont morts (mort-nes ou decedes peu apres) ?
        </p>
      </div>
    )}
    
    {editFormData.statut === 'ne' && (!selectedBirth?.agneaux_ids || selectedBirth.agneaux_ids.length === 0) && (
      <>
        <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900/30">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="edit_creer_agneaux"
              checked={editCreerAgneaux}
              onChange={(e) => {
                setEditCreerAgneaux(e.target.checked);
                if (e.target.checked && editAgneauxData.length === 0) {
                  initializeEditAgneaux(selectedBirth?.nombre_agneaux || 1, selectedBirth);
                }
              }}
              className="w-5 h-5 text-slate-700 dark:text-slate-300 rounded"
            />
            <Label htmlFor="edit_creer_agneaux" className="text-base font-medium cursor-pointer">
              Créer les {selectedBirth?.nombre_agneaux || 1} agneau(x) maintenant
            </Label>
          </div>
          <p className="text-sm text-gray-500 mt-1 ml-8">
            Les agneaux seront ajoutés au troupeau avec un lien vers leur mère
          </p>
        </div>

        {editCreerAgneaux && editAgneauxData.length > 0 && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Baby className="w-5 h-5" />
              Informations des agneaux ({editAgneauxData.length})
            </h3>
            {editAgneauxData.map((agneau, index) => (
              <div key={agneau.numero || `agneau-${index}`} className="border rounded-lg p-4 bg-white dark:bg-gray-800 space-y-3">
                <div className="font-medium text-slate-700 dark:text-slate-300">Agneau #{index + 1}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Numéro *</Label>
                    <Input value={agneau.numero} onChange={(e) => updateEditAgneau(index, 'numero', e.target.value)} placeholder="001" required />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nom</Label>
                    <Input value={agneau.nom} onChange={(e) => updateEditAgneau(index, 'nom', e.target.value)} placeholder="Nom (optionnel)" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Sexe *</Label>
                    <Select value={agneau.sexe} onValueChange={(value) => updateEditAgneau(index, 'sexe', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="femelle">Femelle</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Race</Label>
                    <Select value={agneau.race} onValueChange={(value) => updateEditAgneau(index, 'race', value)}>
                      <SelectTrigger><SelectValue placeholder="Race" /></SelectTrigger>
                      <SelectContent>
                        {breeds.map((b) => (
                          <SelectItem key={b.id} value={b.nom}>{b.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Poids (kg)</Label>
                    <Input type="number" step="0.1" value={agneau.poids} onChange={(e) => updateEditAgneau(index, 'poids', e.target.value)} placeholder="0.0" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    )}

    {selectedBirth?.agneaux_ids && selectedBirth.agneaux_ids.length > 0 && (
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          {selectedBirth.agneaux_ids.length} agneau(x) déjà enregistré(s) pour cette naissance
        </p>
      </div>
    )}
  </>
);
